/**
 * EarnedSchedulePanel — Earned Schedule (ES) metrics panel
 * for the Schedule subtab within Performance.
 *
 * Professional tier only — renders only when PreflightItems have
 * gantt_task_id linkages (i.e., cost data is connected to WBS tasks).
 *
 * Earned Schedule is the time-based counterpart to traditional EVM.
 * Traditional SV ($) converges to zero near project completion regardless
 * of how late the project actually is. ES fixes this by expressing
 * schedule performance in TIME units.
 *
 * Computes from existing data (no new API):
 *   BAC   = Σ estimated_total (total planned budget for linked items)
 *   PV    = Σ estimated_total × (elapsed % of each task's duration)
 *   EV    = Σ estimated_total × (task percentComplete / 100)
 *   AT    = actual elapsed project time (days from project start to today)
 *   ES    = the point in project time when current EV would have been
 *           achieved per the plan (interpolated from the PV curve)
 *   SV(t) = ES − AT         (positive = ahead of schedule, in days)
 *   SPI(t)= ES / AT         (>1 = ahead of schedule)
 *
 * Color semantics follow CLAUDE.md reserved palette:
 *   Green  = healthy (SPI(t) ≥ 1.0, or SV(t) ≥ 0)
 *   Yellow = warning (SPI(t) 0.9–1.0)
 *   Red    = critical (SPI(t) < 0.9)
 *   Blue   = complete (100% done)
 *   Grey   = N/A (insufficient data)
 */

import React, { useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { preflightStore, storeKey } from "../../Data/store";
import { usePreflightLoader } from "../../hooks/usePreflightLoader";
import type { PreflightItem } from "../../Data/PreFlight/preflightTypes";
import type { GanttTask } from "../../api/ganttTasks";
import styles from "./Navigation.module.css";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface EarnedSchedulePanelProps {
  projectGuid: string;
  /** Gantt tasks for the project */
  scheduleTasks: GanttTask[];
  /** Project start date (ISO) — needed for AT calculation */
  projectStart: string | null;
  /** Project end date (ISO) — needed for planned duration */
  projectEnd: string | null;
  debugMode?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Earned Schedule Computation Engine                                 */
/* ------------------------------------------------------------------ */

export interface EarnedScheduleMetrics {
  /** Actual Time — days elapsed from project start to today */
  at: number;
  /** Planned Duration — total project duration in days */
  pd: number;
  /** Earned Schedule — the project day when current EV would have been
   *  achieved per the plan. Interpolated from task-level PV curve. */
  es: number;
  /** Schedule Variance (time) = ES − AT — in days. Positive = ahead. */
  svt: number;
  /** Schedule Performance Index (time) = ES / AT. >1 = ahead. */
  spiT: number | null;
  /** Estimated project completion date based on SPI(t) */
  estimatedFinish: Date | null;
  /** Planned project finish date */
  plannedFinish: Date | null;
  /** Delay in days (negative = ahead, positive = behind) */
  delayDays: number | null;
  /** Overall weighted % complete */
  weightedPctComplete: number;
  /** Number of preflight items linked to Gantt tasks */
  linkedItemCount: number;
  /** Total tasks in schedule */
  totalTasks: number;
  /** Tasks completed (percentComplete >= 100) */
  completedTasks: number;
  /** True when all earned value equals or exceeds BAC (project complete) */
  isComplete: boolean;
}

/**
 * Compute Earned Schedule metrics.
 *
 * The key insight: we build a cumulative PV curve over project time,
 * then find where the current total EV falls on that curve. That
 * time point is ES (Earned Schedule).
 *
 * For the PV curve, each linked preflight item contributes its
 * estimated_total spread linearly across its linked task's duration.
 * We sample this curve at daily granularity.
 */
export function computeEarnedSchedule(
  items: PreflightItem[],
  tasks: GanttTask[],
  projectStart: string | null,
  projectEnd: string | null,
): EarnedScheduleMetrics {
  const empty: EarnedScheduleMetrics = {
    at: 0, pd: 0, es: 0, svt: 0, spiT: null,
    estimatedFinish: null, plannedFinish: null, delayDays: null,
    weightedPctComplete: 0, linkedItemCount: 0,
    totalTasks: tasks.length,
    completedTasks: tasks.filter(t => t.percentComplete >= 100 && !t.isSummary).length,
    isComplete: false, // DEV-963: required field added to EarnedScheduleMetrics
  };

  if (!projectStart || !projectEnd) return empty;
  const projStartMs = Date.parse(projectStart);
  const projEndMs = Date.parse(projectEnd);
  if (isNaN(projStartMs) || isNaN(projEndMs) || projEndMs <= projStartMs) return empty;

  const now = Date.now();
  const msPerDay = 86400000;
  const pd = Math.round((projEndMs - projStartMs) / msPerDay);

  // Build task lookup (case-insensitive)
  const taskMap = new Map<string, GanttTask>();
  for (const t of tasks) {
    if (t.id) taskMap.set(t.id.toUpperCase(), t);
  }

  // Collect linked items with their task schedule data.
  // Also track the latest task end date — used for AT when all work is done.
  interface LinkedItem {
    est: number;
    taskStartDay: number;  // day offset from project start
    taskEndDay: number;
    pctComplete: number;
  }
  const linked: LinkedItem[] = [];
  let totalEv = 0;
  let bac = 0;
  let weightedPctNum = 0;
  let weightedPctDen = 0;

  for (const item of items) {
    if (item.state === "withdrawn") continue;
    if (!item.gantt_task_id) continue;

    const task = taskMap.get(item.gantt_task_id.toUpperCase());
    if (!task) continue;

    const est = item.estimated_total ?? (
      item.unit_cost != null && item.target_quantity != null
        ? item.unit_cost * item.target_quantity
        : 0
    );
    if (est <= 0) continue;

    const taskStartMs = task.startDate ? Date.parse(task.startDate) : projStartMs;
    const taskEndMs = task.endDate ? Date.parse(task.endDate) : projEndMs;
    if (isNaN(taskStartMs) || isNaN(taskEndMs)) continue;

    const taskStartDay = Math.max(0, Math.round((taskStartMs - projStartMs) / msPerDay));
    const taskEndDay = Math.max(taskStartDay + 1, Math.round((taskEndMs - projStartMs) / msPerDay));
    const pctComplete = Math.min(100, Math.max(0, task.percentComplete)) / 100;

    linked.push({ est, taskStartDay, taskEndDay, pctComplete });
    bac += est;
    totalEv += est * pctComplete;

    weightedPctDen += est;
    weightedPctNum += est * pctComplete;
  }

  if (linked.length === 0) return empty;

  const isComplete = totalEv >= bac && bac > 0;
  const at = Math.max(0, Math.round((now - projStartMs) / msPerDay));

  const weightedPctComplete = weightedPctDen > 0
    ? Math.round((weightedPctNum / weightedPctDen) * 100)
    : 0;

  const nonSummaryTasks = tasks.filter(t => !t.isSummary);
  const plannedFinish = new Date(projEndMs);

  // ── Completed project: all earned value equals budget ──
  // ES-based forecasting is meaningless once EV ≥ BAC. We don't have a
  // reliable "actual finish date" (only scheduled task end dates), so
  // rather than compute a misleading retrospective SPI(t), we report
  // the simple facts: work is done, SPI(t) = 1.00, SV(t) = 0.
  if (isComplete) {
    return {
      at, pd, es: pd, svt: 0,
      spiT: 1.0,
      estimatedFinish: null, plannedFinish, delayDays: null,
      weightedPctComplete,
      linkedItemCount: linked.length,
      totalTasks: nonSummaryTasks.length,
      completedTasks: nonSummaryTasks.filter(t => t.percentComplete >= 100).length,
      isComplete: true,
    };
  }

  // ── In-progress project: compute full ES metrics ──

  // Build cumulative PV curve: for each project day, sum the PV contributions
  // from all linked items. Each item distributes its estimated_total linearly
  // across its task duration.
  const totalDays = pd + 1;
  const pvCurve = new Float64Array(totalDays);

  for (const li of linked) {
    const taskDuration = li.taskEndDay - li.taskStartDay;
    if (taskDuration <= 0) continue;
    const dailyPv = li.est / taskDuration;

    for (let d = li.taskStartDay; d < Math.min(li.taskEndDay, totalDays); d++) {
      pvCurve[d] += dailyPv;
    }
  }

  // Convert to cumulative PV
  for (let d = 1; d < totalDays; d++) {
    pvCurve[d] += pvCurve[d - 1];
  }

  // Find ES: the day on the PV curve where cumulative PV equals current total EV.
  // Interpolate between days for precision.
  let es = 0;
  if (totalEv <= 0) {
    es = 0;
  } else {
    // Find the first day where cumulative PV >= totalEv
    for (let d = 0; d < totalDays; d++) {
      if (pvCurve[d] >= totalEv) {
        if (d === 0) {
          es = 0;
        } else {
          // Linear interpolation between day d-1 and d
          const pvPrev = pvCurve[d - 1];
          const pvCurr = pvCurve[d];
          const frac = pvCurr > pvPrev
            ? (totalEv - pvPrev) / (pvCurr - pvPrev)
            : 0;
          es = (d - 1) + frac;
        }
        break;
      }
    }
    // If we never found it (EV > max PV somehow), cap at pd
    if (es === 0 && totalEv > 0 && pvCurve[totalDays - 1] < totalEv) {
      es = pd;
    }
  }

  const svt = es - at;
  const spiT = at > 0 ? es / at : null;

  let estimatedFinish: Date | null = null;
  let delayDays: number | null = null;

  if (spiT != null && spiT > 0) {
    const estimatedDuration = pd / spiT;
    estimatedFinish = new Date(projStartMs + estimatedDuration * msPerDay);
    delayDays = Math.round(estimatedDuration - pd);
  }

  return {
    at, pd, es: Math.round(es * 10) / 10, svt: Math.round(svt * 10) / 10,
    spiT,
    estimatedFinish, plannedFinish, delayDays,
    weightedPctComplete,
    linkedItemCount: linked.length,
    totalTasks: nonSummaryTasks.length,
    completedTasks: nonSummaryTasks.filter(t => t.percentComplete >= 100).length,
    isComplete: false,
  };
}

/* ------------------------------------------------------------------ */
/*  Status Color Helpers                                               */
/* ------------------------------------------------------------------ */

function indexColor(val: number | null): string {
  if (val == null) return "var(--text-muted)";
  if (val >= 1.0) return "var(--color-success)";
  if (val >= 0.9) return "var(--color-warning)";
  return "var(--color-error)";
}

function varianceColorDays(days: number): string {
  if (days >= 0) return "var(--color-success)";
  if (days >= -7) return "var(--color-warning)";
  return "var(--color-error)";
}

function fmtIndex(val: number | null): string {
  if (val == null) return "—";
  return val.toFixed(2);
}

function fmtDays(days: number): string {
  const prefix = days >= 0 ? "+" : "";
  const absDays = Math.abs(days);
  if (absDays < 1) return `${prefix}${days.toFixed(1)}d`;
  return `${prefix}${Math.round(days)}d`;
}

function fmtDate(d: Date | null): string {
  if (!d) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const EarnedSchedulePanel: React.FC<EarnedSchedulePanelProps> = ({
  projectGuid,
  scheduleTasks,
  projectStart,
  projectEnd,
  debugMode,
}) => {
  const { t: tKpi } = useAppTranslation("kpi");

  /* ── Load preflight items ── */
  const { preflightVersion } = usePreflightLoader(projectGuid, true);

  const items: PreflightItem[] = useMemo(() => {
    void preflightVersion;
    if (!projectGuid) return [];
    return preflightStore.get(storeKey(projectGuid)) || [];
  }, [projectGuid, preflightVersion]);

  /* ── Compute Earned Schedule metrics ── */
  const metrics = useMemo(
    () => computeEarnedSchedule(items, scheduleTasks, projectStart, projectEnd),
    [items, scheduleTasks, projectStart, projectEnd],
  );

  /* ── Gate: only render if there are Gantt-linked cost items ── */
  if (metrics.linkedItemCount === 0) return null;

  /* ── Render ── */
  return (
    <div className={styles.breakdownPanel}>
      {/* Section header */}
      <div className={styles.breakdownTitle}>
        {tKpi("schedule.es.title")}
      </div>

      {/* Subtitle */}
      <div style={{
        fontSize: 11,
        color: "var(--text-tertiary)",
        marginBlockEnd: 10,
      }}>
        {tKpi("schedule.es.coverage", {
          count: metrics.linkedItemCount,
        })}
      </div>

      {/* Primary metric row: SPI(t) + SV(t) */}
      <div className={styles.evmMetricRow}>
        <div className={styles.evmMetricCard}>
          <span className={styles.evmMetricLabel}>
            {tKpi("schedule.es.spiT")}
          </span>
          <span
            className={styles.evmMetricValue}
            style={{
              color: metrics.isComplete
                ? "var(--info-text)"          /* Blue = complete */
                : indexColor(metrics.spiT),
            }}
          >
            {metrics.isComplete
              ? fmtIndex(metrics.spiT)        /* Retrospective final SPI(t) */
              : fmtIndex(metrics.spiT)}
          </span>
          <span className={styles.evmMetricHint}>
            {metrics.isComplete
              ? tKpi("schedule.es.spiTFinal")
              : tKpi("schedule.es.spiTHint")}
          </span>
        </div>
        <div className={styles.evmMetricCard}>
          <span className={styles.evmMetricLabel}>
            {tKpi("schedule.es.svt")}
          </span>
          <span
            className={styles.evmMetricValue}
            style={{
              color: metrics.isComplete
                ? "var(--info-text)"
                : varianceColorDays(metrics.svt),
            }}
          >
            {metrics.isComplete
              ? tKpi("schedule.es.complete")
              : fmtDays(metrics.svt)}
          </span>
          <span className={styles.evmMetricHint}>
            {metrics.isComplete
              ? tKpi("schedule.es.actualDuration", {
                  actual: metrics.at,
                  planned: metrics.pd,
                })
              : tKpi("schedule.es.svtHint")}
          </span>
        </div>
      </div>

      {/* Detail table */}
      <table className={styles.breakdownTable}>
        <thead>
          <tr>
            <th className={styles.breakdownTh} style={{ textAlign: "start" }}>
              {tKpi("schedule.es.metric")}
            </th>
            <th className={styles.breakdownTh}>
              {tKpi("schedule.es.value")}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className={styles.breakdownTr}>
            <td className={styles.breakdownTdName}>
              {tKpi("schedule.es.esLabel")}
            </td>
            <td className={styles.breakdownTdNum}>
              {Math.round(metrics.es)} {tKpi("schedule.es.days")}
            </td>
          </tr>
          <tr className={styles.breakdownTr}>
            <td className={styles.breakdownTdName}>
              {tKpi("schedule.es.atLabel")}
            </td>
            <td className={styles.breakdownTdNum}>
              {metrics.at} {tKpi("schedule.es.days")}
            </td>
          </tr>
          <tr className={styles.breakdownTr}>
            <td className={styles.breakdownTdName}>
              {tKpi("schedule.es.pdLabel")}
            </td>
            <td className={styles.breakdownTdNum}>
              {metrics.pd} {tKpi("schedule.es.days")}
            </td>
          </tr>
          <tr className={styles.breakdownTr}>
            <td className={styles.breakdownTdName}>
              {tKpi("schedule.es.plannedFinish")}
            </td>
            <td className={styles.breakdownTdNum}>
              {fmtDate(metrics.plannedFinish)}
            </td>
          </tr>
          {metrics.estimatedFinish && (
            <tr className={styles.breakdownTr}>
              <td className={styles.breakdownTdName}>
                {tKpi("schedule.es.estimatedFinish")}
              </td>
              <td className={styles.breakdownTdNum} style={{
                color: metrics.delayDays != null
                  ? varianceColorDays(-metrics.delayDays)
                  : undefined,
              }}>
                {fmtDate(metrics.estimatedFinish)}
                {metrics.delayDays != null && metrics.delayDays !== 0 && (
                  <span style={{ fontSize: 10, marginInlineStart: 6, opacity: 0.8 }}>
                    ({metrics.delayDays > 0 ? "+" : ""}{metrics.delayDays}d)
                  </span>
                )}
              </td>
            </tr>
          )}
          <tr className={styles.breakdownTr}>
            <td className={styles.breakdownTdName}>
              {tKpi("schedule.es.tasksComplete")}
            </td>
            <td className={styles.breakdownTdNum}>
              {metrics.completedTasks} / {metrics.totalTasks}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Progress bar */}
      <div className={styles.coverageRow}>
        <div className={styles.coverageHeader}>
          <span className={styles.coverageLabel}>
            {tKpi("schedule.es.progress")}
          </span>
          <span className={styles.coverageValue}>
            {metrics.weightedPctComplete}%
          </span>
        </div>
        <div className={styles.coverageTrack}>
          <div
            className={styles.coverageFill}
            style={{
              width: `${Math.min(100, metrics.weightedPctComplete)}%`,
              background: metrics.weightedPctComplete >= 100
                ? "var(--info-text)"
                : "var(--color-success)",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default EarnedSchedulePanel;
